import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ExternalLink, Ticket } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getImageUrl, handleImageError } from '../services/api';

const ConferenceCard = ({ conference }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700">
      <Link to={`/conferences/${conference.slug || conference.id}`}>
        <div className="relative h-48 overflow-hidden">
          <img
            src={getImageUrl(conference.coverImage)}
            alt={conference.title}
            onError={handleImageError}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
          <div className="absolute top-4 left-4">
            {conference.categories.map((cat, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 mb-2 mr-2"
              >
                {cat}
              </Badge>
            ))}
          </div>
        </div>
      </Link>

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Calendar className="w-4 h-4 mr-2" />
            {formatDate(conference.date)}
          </div>
        </div>

        <div className="flex items-center text-sm text-teal-600 dark:text-teal-400 mb-3">
          <MapPin className="w-4 h-4 mr-2" />
          {conference.location}
        </div>

        <a
          href={conference.websiteUrl?.startsWith('http') ? conference.websiteUrl : `https://${conference.websiteUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-2 inline-block hover:underline"
        >
          {conference.websiteUrl}
        </a>

        <Link to={`/conferences/${conference.slug || conference.id}`}>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
            {conference.title}
          </h3>
        </Link>

        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
          {conference.description}
        </p>

        <div className="flex items-center justify-between">
          {conference.priceRange && (
            <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">
              {conference.priceRange}
            </Badge>
          )}
          {conference.ticketUrl && (
            <Button
              variant="outline"
              className="text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20"
              onClick={() => window.open(conference.ticketUrl.startsWith('http') ? conference.ticketUrl : `https://${conference.ticketUrl}`, '_blank')}
            >
              <Ticket className="w-4 h-4 mr-2" />
              Buy Tickets
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ConferenceCard;